import { API_BASE } from '../config/api';
import AuthService from './authService';

class CollectionsService {
  static baseURL = `${API_BASE}/api`;
  
  static getCurrentUser() {
    return AuthService.getCurrentUser() || { id: 'guest', name: 'Guest' };
  }

  // Get all collections for current user (DATABASE VERSION)
  static async getCollections(userId = null) {
    try {
      const response = await fetch(`${this.baseURL}/collections`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await response.json();

      if (data.success) {
        return data.collections || [];
      } else {
        console.error('Failed to get collections from database:', data.message);
        return [];
      }
    } catch (error) {
      console.error('Error getting collections from database:', error);
      return [];
    }
  }


  // Create a new collection (DATABASE VERSION)
  static async createCollection(name, description = '') {
    try {
      const response = await fetch(`${this.baseURL}/collections`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim(),
          color: 'blue'
        })
      });

      const data = await response.json();
      console.log('createCollection response:', response.status, data);

      if (data.success) {
        return { success: true, collection: data.collection, message: data.message };
      } else {
        return { success: false, message: data.error || data.message || 'Failed to create collection' };
      }
    } catch (error) {
      console.error('Error creating collection:', error);
      return { success: false, message: 'Failed to create collection' };
    }
  }

  // Update an existing collection (DATABASE VERSION)
  static async updateCollection(collectionId, updates) {
    try {
      const response = await fetch(`${this.baseURL}/collections/${collectionId}`, {
        method: 'PUT',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify(updates)
      });

      const data = await response.json();

      if (data.success) {
        return { success: true, collection: data.collection, message: data.message };
      } else {
        return { success: false, message: data.error || 'Failed to update collection' };
      }
    } catch (error) {
      console.error('Error updating collection:', error);
      return { success: false, message: 'Failed to update collection' };
    }
  }

  // Delete a collection (DATABASE VERSION)
  static async deleteCollection(collectionId) {
    try {
      const response = await fetch(`${this.baseURL}/collections/${collectionId}`, {
        method: 'DELETE',
        headers: AuthService.getJsonHeaders()
      });

      const data = await response.json();

      if (data.success) {
        return { success: true, message: data.message };
      } else {
        return { success: false, message: data.error || 'Failed to delete collection' };
      }
    } catch (error) {
      console.error('Error deleting collection:', error);
      return { success: false, message: 'Failed to delete collection' };
    }
  }

  // Add activity to collection (DATABASE VERSION - async)
  static async addActivityToCollection(collectionId, activityId) {
    try {
      const response = await fetch(`${this.baseURL}/collections/${collectionId}/activities`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({
          activityId: activityId
        })
      });

      const data = await response.json();

      if (data.success) {
        return { success: true, message: data.message };
      } else {
        return { success: false, message: data.error || 'Failed to add activity to collection' };
      }
    } catch (error) {
      console.error('Error adding activity to collection:', error);
      return { success: false, message: 'Failed to add activity to collection' };
    }
  }

  // Add trip to collection (DATABASE VERSION - trips use same activities table)
  static async addTripToCollection(collectionId, tripId) {
    try {
      const response = await fetch(`${this.baseURL}/collections/${collectionId}/activities`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({
          activityId: tripId  // Trips are stored as activities in the database
        })
      });

      const data = await response.json();

      if (data.success) {
        return { success: true, message: data.message || 'Trip added to collection' };
      } else {
        return { success: false, message: data.error || 'Failed to add trip to collection' };
      }
    } catch (error) {
      console.error('Error adding trip to collection:', error);
      return { success: false, message: 'Failed to add trip to collection' };
    }
  }

  // Remove activity from collection (DATABASE VERSION - async)
  static async removeActivityFromCollection(collectionId, activityId) {
    try {
      const response = await fetch(`${this.baseURL}/collections/${collectionId}/activities/${activityId}`, {
        method: 'DELETE',
        headers: AuthService.getJsonHeaders()
      });

      const data = await response.json();

      if (data.success) {
        return { success: true, message: data.message };
      } else {
        return { success: false, message: data.error || 'Failed to remove activity from collection' };
      }
    } catch (error) {
      console.error('Error removing activity from collection:', error);
      return { success: false, message: 'Failed to remove activity from collection' };
    }
  }

  // Move activity between collections (DATABASE VERSION - async)
  static async moveActivityToCollection(activityId, fromCollectionId, toCollectionId) {
    try {
      if (fromCollectionId) {
        const removeResult = await this.removeActivityFromCollection(fromCollectionId, activityId);
        if (!removeResult.success) {
          return removeResult;
        }
      }

      if (toCollectionId) {
        return await this.addActivityToCollection(toCollectionId, activityId);
      }

      return { success: true, message: 'Activity removed from collection' };
    } catch (error) {
      console.error('Error moving activity between collections:', error);
      return { success: false, message: 'Failed to move activity between collections' };
    }
  }

  // Get collection by ID (from database)
  // Fetch source attribution for a copied collection. Returns:
  //   { hasSource: false }  — collection was created directly
  //   { hasSource: true, available: false }  — source trip is gone/private
  //   { hasSource: true, available: true, trip_id, trip_name,
  //     trip_owner_name, collection_id, collection_name }
  // Null on network/server error.
  static async getCollectionSource(collectionId) {
    try {
      const response = await fetch(`${this.baseURL}/collections/${collectionId}/source`, {
        headers: AuthService.getJsonHeaders()
      });
      const data = await response.json();
      if (!data.success) return null;
      return data;
    } catch (error) {
      console.error('Error getting collection source:', error);
      return null;
    }
  }

  static async getCollectionById(collectionId) {
    try {
      const response = await fetch(`${this.baseURL}/collections/${collectionId}`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await response.json();

      if (data.success) {
        return data.collection;
      } else {
        console.error('Failed to get collection:', data.error);
        return null;
      }
    } catch (error) {
      console.error('Error getting collection by ID:', error);
      return null;
    }
  }

  // Get which collection an activity belongs to
  static async getActivityCollection(activityId) {
    const collections = await this.getCollections();
    return collections.find(collection => {
      const activityIds = collection.activityIds || [];
      return activityIds.includes(activityId);
    }) || null;
  }

  // Get color classes for a collection - flat pale primary tint
  // (the old ocean gradient was retired in the 2026-10 visual refresh).
  // No callers at the moment.
  static getColorClasses() {
    return {
      bg: 'bg-primary-lighter',
      text: 'text-primary-dark',
      border: 'border-primary',
      hover: 'hover:bg-primary-light/30',
      iconBg: 'bg-primary-light/30'
    };
  }

  // =====================================================
  // SAVED COLLECTIONS (from other users)
  // =====================================================

  /**
   * Get collections saved by the current user (from other users)
   * @returns {array} Array of saved collections with creator info
   */
  static async getSavedCollections() {
    try {
      const response = await fetch(`${this.baseURL}/collections/saved`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await response.json();

      if (data.success) {
        return data.collections || [];
      } else {
        console.error('Failed to get saved collections:', data.error);
        return [];
      }
    } catch (error) {
      console.error('Error getting saved collections:', error);
      return [];
    }
  }

  /**
   * Get all collections for user: owned + saved from others (deduplicated)
   * @returns {array} Array of all collections with ownership flags
   */
  static async getAllUserCollections() {
    try {
      const [ownedCollections, savedCollections] = await Promise.all([
        this.getCollections(),
        this.getSavedCollections()
      ]);

      // Mark owned collections
      const ownedWithFlag = ownedCollections.map(col => ({
        ...col,
        is_owned: true
      }));

      // Saved collections already have is_owned: false and is_saved_from_other: true
      // Dedupe: filter out any saved collections that somehow have same ID as owned
      const ownedIds = new Set(ownedCollections.map(c => String(c.id)));
      const uniqueSavedCollections = savedCollections.filter(c => !ownedIds.has(String(c.id)));

      // Combine: owned first, then saved
      return [...ownedWithFlag, ...uniqueSavedCollections];
    } catch (error) {
      console.error('Error getting all user collections:', error);
      return [];
    }
  }

  /**
   * Enable sharing for a collection (generates a share code, sets visibility to friends)
   * @param {string} collectionId - Collection ID to share
   * @returns {object} { success, shareCode, shareUrl, message } | { success: false, message }
   *   shareUrl is the server-relative path (/shared/collection/<code>); callers
   *   that need a copyable link should prefix it with window.location.origin.
   */
  static async shareCollection(collectionId) {
    try {
      const response = await fetch(`${this.baseURL}/collections/${collectionId}/share`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders()
      });

      const data = await response.json();

      if (data.success) {
        return { success: true, shareCode: data.shareCode, shareUrl: data.shareUrl, message: data.message };
      } else {
        return { success: false, message: data.error || 'Failed to share collection' };
      }
    } catch (error) {
      console.error('Error sharing collection:', error);
      return { success: false, message: 'Failed to share collection' };
    }
  }

  /**
   * Stop sharing a collection (revert to private). The public link stops working
   * and it leaves Discover, but the share code is kept so re-sharing revives the
   * same URL.
   * @param {string} collectionId - Collection ID to unshare
   * @returns {object} { success, message } | { success: false, message }
   */
  static async unshareCollection(collectionId) {
    try {
      const response = await fetch(`${this.baseURL}/collections/${collectionId}/share`, {
        method: 'DELETE',
        headers: AuthService.getJsonHeaders()
      });

      const data = await response.json();

      if (data.success) {
        return { success: true, message: data.message };
      } else {
        return { success: false, message: data.error || 'Failed to stop sharing collection' };
      }
    } catch (error) {
      console.error('Error unsharing collection:', error);
      return { success: false, message: 'Failed to stop sharing collection' };
    }
  }

  /**
   * Send a collection to a friend (private push). The recipient gets a
   * notification and, on accept, a private copy in their own collections. The
   * source collection's visibility is unchanged. Recipient must be a friend.
   * @param {string} collectionId
   * @param {string} recipientUserId
   * @returns {object} { success, message } | { success: false, message }
   */
  static async sendCollection(collectionId, recipientUserId) {
    try {
      const response = await fetch(`${this.baseURL}/collections/${collectionId}/send`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ recipientUserId })
      });

      const data = await response.json();

      if (data.success) {
        return { success: true, message: data.message };
      } else {
        return { success: false, message: data.error || 'Failed to send collection' };
      }
    } catch (error) {
      console.error('Error sending collection:', error);
      return { success: false, message: 'Failed to send collection' };
    }
  }

  /**
   * Save a collection reference (bookmark another user's collection)
   * @param {string} collectionId - Collection ID to save
   * @returns {object} Result
   */
  static async saveCollection(collectionId) {
    try {
      const response = await fetch(`${this.baseURL}/collections/${collectionId}/save`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders()
      });

      const data = await response.json();

      if (data.success) {
        return { success: true, message: data.message };
      } else {
        return { success: false, message: data.error || 'Failed to save collection' };
      }
    } catch (error) {
      console.error('Error saving collection:', error);
      return { success: false, message: 'Failed to save collection' };
    }
  }

  /**
   * Unsave a collection reference (remove bookmark)
   * @param {string} collectionId - Collection ID to unsave
   * @returns {object} Result
   */
  static async unsaveCollection(collectionId) {
    try {
      const response = await fetch(`${this.baseURL}/collections/${collectionId}/save`, {
        method: 'DELETE',
        headers: AuthService.getJsonHeaders()
      });

      const data = await response.json();

      if (data.success) {
        return { success: true, message: data.message };
      } else {
        return { success: false, message: data.error || 'Failed to unsave collection' };
      }
    } catch (error) {
      console.error('Error unsaving collection:', error);
      return { success: false, message: 'Failed to unsave collection' };
    }
  }

  /**
   * Check if a collection is saved by the current user
   * @param {string} collectionId - Collection ID to check
   * @returns {object} Result with isSaved boolean
   */
  static async isCollectionSaved(collectionId) {
    try {
      const response = await fetch(`${this.baseURL}/collections/${collectionId}/is-saved`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await response.json();

      if (data.success) {
        return { success: true, isSaved: data.isSaved };
      } else {
        return { success: false, isSaved: false };
      }
    } catch (error) {
      console.error('Error checking if collection is saved:', error);
      return { success: false, isSaved: false };
    }
  }

  /**
   * Archive a collection
   * @param {string} collectionId - Collection ID to archive
   * @returns {object} Result object
   */
  // makePrivate is the opt-in hard cut: archiving withdraws a collection from
  // browsing and discovery but leaves its share link and existing saves alone,
  // so flipping visibility is the only thing that actually revokes access.
  static async archiveCollection(collectionId, makePrivate = false) {
    try {
      const response = await fetch(`${this.baseURL}/collections/${collectionId}/archive`, {
        method: 'PUT',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ make_private: makePrivate })
      });

      return await response.json();
    } catch (error) {
      console.error('Error archiving collection:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Unarchive (restore) a collection
   * @param {string} collectionId - Collection ID to restore
   * @returns {object} Result object
   */
  static async unarchiveCollection(collectionId) {
    try {
      const response = await fetch(`${this.baseURL}/collections/${collectionId}/unarchive`, {
        method: 'PUT',
        headers: AuthService.getJsonHeaders()
      });

      return await response.json();
    } catch (error) {
      console.error('Error unarchiving collection:', error);
      return { success: false, error: error.message };
    }
  }

}

export default CollectionsService;